import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/authStore';
import { 
  BookOpen, 
  Languages, 
  Users, 
  Receipt, 
  Search, 
  SlidersHorizontal, 
  ChevronLeft, 
  ChevronRight, 
  Star, 
  Clock, 
  ExternalLink, 
  Bell, 
  Sparkles,
  ArrowRight,
  TrendingUp,
  Building2,
  Handshake,
  ShieldCheck,
  CheckCircle2,
  Phone,
  Mail,
  Layers,
  Globe2,
  UserCheck
} from 'lucide-react';
import { PartnerRecord, TranslationProject, Publication, StaffMember, CustomerRecord } from '@ftt/shared';

export const AdminDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'ALL' | 'TRANSLATION' | 'REVIEW' | 'PUBLISHED'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('All');
  const [carouselIndex, setCarouselIndex] = useState(0);

  // 1. Live Metrics
  const { data: metricsRes, isLoading: metricsLoading } = useQuery({
    queryKey: ['admin-metrics'],
    queryFn: () => api.get('/admin/metrics').then((res) => res.data.data),
  });

  // 2. Live Publications
  const { data: publicationsRes } = useQuery({
    queryKey: ['admin-publications-dash'],
    queryFn: () => api.get('/public/publications').then((res) => res.data.data),
  });

  // 3. Live Translations
  const { data: translationsRes } = useQuery({
    queryKey: ['admin-translations-dash'],
    queryFn: () => api.get('/admin/translations').then((res) => res.data.data),
  });

  // 4. Live Partners
  const { data: partnersRes } = useQuery({
    queryKey: ['admin-partners-dash'],
    queryFn: () => api.get('/admin/partners').then((res) => res.data.data),
  });

  // 5. Live Customers
  const { data: customersRes } = useQuery({
    queryKey: ['admin-customers-dash'],
    queryFn: () => api.get('/admin/customers').then((res) => res.data.data),
  });

  // 6. Live Staff
  const { data: staffRes } = useQuery({
    queryKey: ['admin-staff-dash'],
    queryFn: () => api.get('/admin/staff').then((res) => res.data.data),
  });

  const metrics = metricsRes || {
    totalEnquiries: 6,
    newEnquiries: 2,
    activeClients: 4,
    activeProjects: 5,
    activeTranslations: 3,
    activePartners: 6,
    activeStaff: 7,
    publishedWorks: 3,
    financials: {
      totalContractedRevenueINR: 3890000,
      totalReceivedRevenueINR: 2480000,
      totalOutstandingINR: 1410000,
      totalProductionCostINR: 1120000,
      netProfitINR: 1360000,
    },
  };

  const publications: Publication[] = publicationsRes || [];
  const translations: TranslationProject[] = translationsRes || [];
  const partnersList: PartnerRecord[] = partnersRes || [];
  const customersList: CustomerRecord[] = customersRes || [];
  const staffList: StaffMember[] = staffRes || [];

  // Real Curated Book Jackets with High-Contrast Text
  const bookShelves = [
    {
      id: 'book-01',
      title: 'INTER BEAUTY',
      displayTitle: 'The Message of Acts',
      author: 'John Stott',
      jacketClass: 'bg-gradient-to-b from-[#C43838] via-[#AD2828] to-[#801717]',
      jacketSubtitle: 'ACTS EXPOSITION / IVP',
      genre: 'Biblical Studies',
      rating: '4.9',
      durationOrPages: '10 Chapters',
      status: 'PUBLISHED',
      languages: 'Hindi & English',
      storeUrl: 'https://forthetruth.in/product/the-message-of-acts',
      tag: 'Store Live',
    },
    {
      id: 'book-02',
      title: 'BEAUTY OF JOLES',
      displayTitle: 'The Trinity',
      author: 'Donald Fairbairn',
      jacketClass: 'bg-gradient-to-b from-[#C58B3E] via-[#B27324] to-[#854F11]',
      jacketSubtitle: 'TRINITARIAN DOCTRINE',
      genre: 'Systematic Theology',
      rating: '4.8',
      durationOrPages: '8 Chapters',
      status: 'TRANSLATION',
      languages: 'Tamil & English',
      storeUrl: 'https://forthetruth.in/product/the-trinity/',
      tag: 'Tamil Draft',
    },
    {
      id: 'book-03',
      title: 'FOUNDING',
      displayTitle: 'Justification: An Intro',
      author: 'Thomas R. Schreiner',
      jacketClass: 'bg-gradient-to-b from-[#8EAF45] via-[#76972E] to-[#4E6B17]',
      jacketSubtitle: 'SOTERIOLOGY / CROSSWAY',
      genre: 'Doctrine of Grace',
      rating: '5.0',
      durationOrPages: '12 Chapters',
      status: 'TRANSLATION',
      languages: 'Telugu & English',
      storeUrl: 'https://forthetruth.in/product/justification-an-introduction',
      tag: 'Telugu Draft',
    },
    {
      id: 'book-04',
      title: 'INTER BEAUTY',
      displayTitle: 'The Gospel for Real Life',
      author: 'Jerry Bridges',
      jacketClass: 'bg-gradient-to-b from-[#3DA9B5] via-[#2A8E9A] to-[#16646E]',
      jacketSubtitle: 'DISCIPLESHIP / NAVPRESS',
      genre: 'Christian Living',
      rating: '4.9',
      durationOrPages: '14 Chapters',
      status: 'REVIEW',
      languages: 'Malayalam & English',
      storeUrl: 'https://forthetruth.in/books',
      tag: 'Malayalam Review',
    },
    {
      id: 'book-05',
      title: 'WELCOME TO JUNGLE',
      displayTitle: 'The Crook in the Lot',
      author: 'Thomas Boston',
      jacketClass: 'bg-gradient-to-b from-[#4A64BF] via-[#334FA8] to-[#1E3480]',
      jacketSubtitle: 'SOVEREIGNTY & TRIAL',
      genre: 'Puritan Classics',
      rating: '4.7',
      durationOrPages: '7 Chapters',
      status: 'TRANSLATION',
      languages: 'Bengali & English',
      storeUrl: 'https://forthetruth.in/books',
      tag: 'Bengali Draft',
    },
    {
      id: 'book-06',
      title: 'WELCOME TO JUNGLE',
      displayTitle: 'The Mortification of Sin',
      author: 'Dr. John Owen',
      jacketClass: 'bg-gradient-to-b from-[#A547BD] via-[#8E30A6] to-[#651779]',
      jacketSubtitle: 'SANCTIFICATION / BANNER',
      genre: 'Puritan Classics',
      rating: '4.9',
      durationOrPages: '14 Chapters',
      status: 'REVIEW',
      languages: 'Hindi & English',
      storeUrl: 'https://forthetruth.in/books',
      tag: 'Hindi Polish',
    },
  ];

  // In-Progress Works (Top Right Card)
  const inProgressProjects = [
    {
      id: 'ip-1',
      title: 'The Message of Acts',
      author: 'John Stott',
      jacketClass: 'bg-[#C43838]',
      jacketText: 'INTER BEAUTY',
      progressRatio: '8 / 10 Chapters',
      progressPercent: 80,
      language: 'Hindi Vernacular Translation',
      color: 'bg-[#C43838]',
    },
    {
      id: 'ip-2',
      title: 'The Trinity',
      author: 'Donald Fairbairn',
      jacketClass: 'bg-[#C58B3E]',
      jacketText: 'BEAUTY OF JOLES',
      progressRatio: '4 / 8 Chapters',
      progressPercent: 50,
      language: 'Tamil Vernacular Translation',
      color: 'bg-[#C58B3E]',
    },
    {
      id: 'ip-3',
      title: 'Justification: An Intro',
      author: 'Thomas Schreiner',
      jacketClass: 'bg-[#4A64BF]',
      jacketText: 'WELCOME TO JUNGLE',
      progressRatio: '9 / 12 Chapters',
      progressPercent: 75,
      language: 'Telugu Vernacular Translation',
      color: 'bg-[#4A64BF]',
    },
  ];

  // Category Breakdown (Bottom Left Card)
  const demographicCategories = [
    { name: 'Biblical Studies & Commentaries', ratio: '09/16', percent: 56, color: '#A855F7' },
    { name: 'Systematic & Historic Theology', ratio: '08/14', percent: 57, color: '#F43F5E' },
    { name: 'Christian Living & Discipleship', ratio: '03/17', percent: 18, color: '#F59E0B' },
    { name: 'Academic Books & Monographs', ratio: '06/18', percent: 33, color: '#84CC16' },
    { name: 'Indian Vernacular Translations', ratio: '06/13', percent: 46, color: '#D946EF' },
    { name: 'Pastoral Ministry & Leadership', ratio: '02/11', percent: 18, color: '#3B82F6' },
    { name: 'Puritan & Classic Reprints', ratio: '02/09', percent: 22, color: '#06B6D4' },
  ];

  // Filtered books
  const filteredBooks = bookShelves.filter((b) => {
    if (activeTab === 'TRANSLATION' && b.status !== 'TRANSLATION') return false;
    if (activeTab === 'REVIEW' && b.status !== 'REVIEW') return false;
    if (activeTab === 'PUBLISHED' && b.status !== 'PUBLISHED') return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return b.displayTitle.toLowerCase().includes(q) || b.author.toLowerCase().includes(q) || b.genre.toLowerCase().includes(q);
    }
    return true;
  });

  const handlePrev = () => {
    setCarouselIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCarouselIndex((prev) => Math.min(filteredBooks.length - 1, prev + 1));
  };

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      
      {/* 1. Top Bar: Search, Language Switcher, Notifications, Profile */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200/90 flex flex-col md:flex-row items-center justify-between gap-4">
        
        {/* Search Pill with High-Contrast Text */}
        <div className="w-full md:w-96 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-xs shadow-inner">
          <Search className="w-4 h-4 text-slate-600 shrink-0" />
          <input
            type="text"
            placeholder="Search publications, authors, translations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-transparent text-slate-900 font-semibold placeholder-slate-500 focus:outline-hidden text-xs"
          />
          <SlidersHorizontal className="w-4 h-4 text-slate-600 shrink-0 cursor-pointer hover:text-slate-900" />
        </div>

        {/* Right Controls */}
        <div className="flex items-center gap-3 w-full md:w-auto justify-end">
          {/* Indian Language Selector */}
          <div className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-300 text-xs font-bold text-slate-800 shadow-2xs">
            <Globe2 className="w-3.5 h-3.5 text-brand-600" />
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="bg-transparent border-none text-xs font-bold text-slate-800 focus:outline-hidden cursor-pointer"
            >
              <option value="All">All Vernaculars</option>
              <option value="Eng">English</option>
              <option value="Hindi">Hindi (हिंदी)</option>
              <option value="Tamil">Tamil (தமிழ்)</option>
              <option value="Telugu">Telugu (తెలుగు)</option>
              <option value="Malayalam">Malayalam (മലയാളം)</option>
              <option value="Bengali">Bengali (বাংলা)</option>
            </select>
          </div>

          {/* Notification Button */}
          <button className="p-2.5 rounded-xl bg-slate-900 text-white hover:bg-slate-800 relative shadow-sm cursor-pointer">
            <Bell className="w-4 h-4" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400 absolute top-1.5 right-1.5 ring-2 ring-slate-900" />
          </button>

          {/* User Profile Pill */}
          <div className="flex items-center gap-3 pl-2 border-l border-slate-300">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-slate-900 to-slate-800 text-white flex items-center justify-center font-extrabold text-xs shadow-sm">
              {user?.firstName?.[0] || 'A'}
            </div>
            <div className="hidden sm:block text-left leading-tight">
              <div className="text-xs font-extrabold text-slate-900">
                {user?.firstName || 'Ashish'} {user?.lastName || 'Admin'}
              </div>
              <div className="text-[10px] font-bold text-brand-700 uppercase tracking-wider">
                FOR THE TRUTH PRESS &bull; {user?.role || 'SUPER_ADMIN'}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* 2. Top 4 High-Contrast Stats Row */}
      <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-sm border border-slate-200/90 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        
        {/* Stat 1: Published Books */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-purple-100 border border-purple-200 flex items-center justify-center text-purple-700 shrink-0 shadow-xs">
            <BookOpen className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 font-serif leading-none">
              {metrics.publishedWorks || 3}
            </div>
            <div className="text-xs text-slate-700 font-bold mt-1">
              Read / Published Books
            </div>
          </div>
        </div>

        {/* Stat 2: In Translation Pipeline */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-700 shrink-0 shadow-xs">
            <Languages className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 font-serif leading-none">
              {metrics.activeTranslations || 3}
            </div>
            <div className="text-xs text-slate-700 font-bold mt-1">
              Are In Translation
            </div>
          </div>
        </div>

        {/* Stat 3: Active Authors & Partners */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-sky-100 border border-sky-200 flex items-center justify-center text-sky-700 shrink-0 shadow-xs">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 font-serif leading-none">
              {(metrics.activePartners || 6) + (metrics.activeClients || 4) + (metrics.activeStaff || 7)}
            </div>
            <div className="text-xs text-slate-700 font-bold mt-1">
              Read Authors &amp; Partners
            </div>
          </div>
        </div>

        {/* Stat 4: Revenue & Realized Pages */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-orange-100 border border-orange-200 flex items-center justify-center text-orange-700 shrink-0 shadow-xs">
            <Receipt className="w-6 h-6" />
          </div>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 font-serif leading-none">
              320/850
            </div>
            <div className="text-xs text-slate-700 font-bold mt-1">
              Read Pages &bull; ₹24.8L Realized
            </div>
          </div>
        </div>

      </div>

      {/* 3. Section 1: Reading Status (Book Shelf) + Keep Reading (Active Projects) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Books Shelf (8 Cols) */}
        <div className="lg:col-span-8 bg-white rounded-3xl p-6 shadow-sm border border-slate-200/90 space-y-6 flex flex-col justify-between">
          
          {/* Shelf Header & Tabs */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-xl font-extrabold text-slate-900">
                Reading Status &amp; Book Progress
              </h2>
              <div className="flex items-center gap-4 pt-2.5 text-xs font-bold">
                <button
                  onClick={() => setActiveTab('ALL')}
                  className={`transition-colors cursor-pointer ${activeTab === 'ALL' ? 'text-[#FF6B4A] border-b-2 border-[#FF6B4A] pb-1 font-extrabold' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setActiveTab('TRANSLATION')}
                  className={`transition-colors cursor-pointer ${activeTab === 'TRANSLATION' ? 'text-[#FF6B4A] border-b-2 border-[#FF6B4A] pb-1 font-extrabold' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  In Progress
                </button>
                <button
                  onClick={() => setActiveTab('REVIEW')}
                  className={`transition-colors cursor-pointer ${activeTab === 'REVIEW' ? 'text-[#FF6B4A] border-b-2 border-[#FF6B4A] pb-1 font-extrabold' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  Theological Review
                </button>
                <button
                  onClick={() => setActiveTab('PUBLISHED')}
                  className={`transition-colors cursor-pointer ${activeTab === 'PUBLISHED' ? 'text-[#FF6B4A] border-b-2 border-[#FF6B4A] pb-1 font-extrabold' : 'text-slate-600 hover:text-slate-900'}`}
                >
                  Published &amp; Store
                </button>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <button
                onClick={handlePrev}
                className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-800 font-bold transition-colors cursor-pointer"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={handleNext}
                className="w-8 h-8 rounded-xl bg-[#FFEDD5] hover:bg-orange-200 flex items-center justify-center text-[#EA580C] font-black transition-colors cursor-pointer"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* 6 Book Cards Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {filteredBooks.map((book) => (
              <a
                key={book.id}
                href={book.storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="space-y-2.5 flex flex-col group cursor-pointer"
              >
                {/* Styled Colored Book Jacket */}
                <div
                  className={`w-full aspect-2/3 rounded-2xl p-3 shadow-md flex flex-col justify-between text-white relative overflow-hidden transition-all duration-300 group-hover:-translate-y-2 group-hover:shadow-xl ${book.jacketClass}`}
                >
                  <div className="absolute left-0 top-0 bottom-0 w-2 bg-black/30" />
                  <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-white/30" />

                  {/* Book Jacket Title */}
                  <div className="relative z-10 pt-2 text-center">
                    <div className="font-serif font-black text-xs leading-tight tracking-wider uppercase drop-shadow-md">
                      {book.title}
                    </div>
                  </div>

                  {/* Bottom Ribbon */}
                  <div className="relative z-10 text-center pb-1">
                    <span className="text-[8px] font-mono font-bold tracking-widest uppercase opacity-90 block">
                      {book.jacketSubtitle}
                    </span>
                  </div>
                </div>

                {/* Metadata with High Visibility */}
                <div className="space-y-0.5 text-left">
                  <h4 className="font-extrabold text-xs text-slate-900 truncate leading-snug group-hover:text-[#EA580C] transition-colors" title={book.displayTitle}>
                    {book.displayTitle}
                  </h4>
                  <div className="flex items-center gap-2 text-[10px] font-bold text-slate-700">
                    <span className="flex items-center gap-0.5 text-amber-600 font-extrabold">
                      <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                      <span>{book.rating}</span>
                    </span>
                    <span className="text-slate-600">&bull;</span>
                    <span className="text-slate-700 font-semibold">{book.durationOrPages}</span>
                  </div>
                  <div className="text-[11px] text-slate-600 font-bold truncate">
                    By {book.author}
                  </div>
                </div>

              </a>
            ))}
          </div>

        </div>

        {/* Keep Reading / Active In-Progress Projects (4 Cols) */}
        <div className="lg:col-span-4 bg-white rounded-3xl p-6 shadow-sm border border-slate-200/90 space-y-5 flex flex-col justify-between">
          
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="font-extrabold text-base text-slate-900">
              Keep Reading
            </h3>
            <Link
              to="/admin/translations"
              className="text-xs font-bold text-[#FF6B4A] hover:underline"
            >
              See All
            </Link>
          </div>

          {/* 3 Active In-Progress Projects */}
          <div className="space-y-4">
            {inProgressProjects.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3.5 p-2 rounded-2xl hover:bg-slate-50 transition-colors"
              >
                {/* Mini Book Cover Thumbnail */}
                <div
                  className={`w-12 h-16 rounded-xl ${item.jacketClass} text-white shrink-0 p-1.5 shadow-md flex flex-col justify-between text-center`}
                >
                  <div className="text-[7px] font-serif font-black uppercase leading-tight pt-1">
                    {item.jacketText}
                  </div>
                  <div className="text-[6px] font-mono font-bold uppercase opacity-80">
                    FTT
                  </div>
                </div>

                {/* Info & Progress Bar */}
                <div className="flex-1 space-y-1.5 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className="font-extrabold text-xs text-slate-900 truncate">
                      {item.title}
                    </h4>
                  </div>
                  <div className="text-[11px] text-slate-700 font-semibold truncate">
                    {item.author} &bull; <strong className="text-brand-700">{item.language}</strong>
                  </div>

                  {/* Horizontal Progress Bar */}
                  <div className="space-y-1">
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden border border-slate-200">
                      <div
                        className={`h-2 rounded-full ${item.color}`}
                        style={{ width: `${item.progressPercent}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-[10px] font-mono font-bold text-slate-700">
                      <span>{item.progressPercent}% Completed</span>
                      <span>{item.progressRatio}</span>
                    </div>
                  </div>
                </div>

              </div>
            ))}
          </div>

          <div className="pt-2">
            <Link
              to="/admin/translations"
              className="w-full py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-900 text-xs font-extrabold text-center flex items-center justify-center gap-2 transition-colors shadow-2xs"
            >
              <span>Manage Vernacular Pipeline</span>
              <ArrowRight className="w-4 h-4 text-[#EA580C]" />
            </Link>
          </div>

        </div>

      </div>

      {/* 4. Section 2: Three Divided Cards for Partner Publishers, Clients CRM, and Internal Staffing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* Card 1: Partner Publishers CRM */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200/90 space-y-4 flex flex-col justify-between">
          <div className="space-y-1 border-b border-slate-100 pb-3 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Handshake className="w-5 h-5 text-blue-600" />
                <h3 className="font-extrabold text-base text-slate-900">
                  Partner Publishers
                </h3>
              </div>
              <p className="text-[11px] text-slate-600 font-semibold">
                Rights holders &amp; co-publishers (Crossway, IVP, Banner)
              </p>
            </div>
            <Link to="/admin/partners" className="text-xs font-bold text-blue-600 hover:underline">
              View All &rarr;
            </Link>
          </div>

          <div className="space-y-2.5">
            {[
              { name: 'Crossway Books (USA)', stage: 'Active Rights', titles: 12, country: 'USA' },
              { name: 'InterVarsity Press (IVP)', stage: 'Active Rights', titles: 8, country: 'UK' },
              { name: 'Banner of Truth Trust', stage: 'Classic Licensing', titles: 6, country: 'UK' },
              { name: 'Baker Academic / B&H', stage: 'Under Review', titles: 4, country: 'USA' },
            ].map((partner, idx) => (
              <div
                key={idx}
                className="p-3 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between text-xs"
              >
                <div>
                  <h4 className="font-extrabold text-slate-900">{partner.name}</h4>
                  <span className="text-[10px] font-bold text-slate-500 font-mono">
                    {partner.titles} Licensed Works &bull; {partner.country}
                  </span>
                </div>
                <span className="px-2.5 py-0.5 rounded-md bg-blue-100 text-blue-800 font-extrabold text-[10px] font-mono">
                  {partner.stage}
                </span>
              </div>
            ))}
          </div>

          <Link
            to="/admin/partners"
            className="w-full py-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-800 text-xs font-bold text-center block transition-colors"
          >
            Manage {partnersList.length || 6} Publishing Partners
          </Link>
        </div>

        {/* Card 2: Translation Clients & Customer CRM */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200/90 space-y-4 flex flex-col justify-between">
          <div className="space-y-1 border-b border-slate-100 pb-3 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" />
                <h3 className="font-extrabold text-base text-slate-900">
                  Translation Clients CRM
                </h3>
              </div>
              <p className="text-[11px] text-slate-600 font-semibold">
                Authors &amp; ministries commissioning translation
              </p>
            </div>
            <Link to="/admin/customers" className="text-xs font-bold text-emerald-600 hover:underline">
              View All &rarr;
            </Link>
          </div>

          <div className="space-y-2.5">
            {[
              { name: 'Reformed Theological Seminary', service: 'Hindi & Tamil Editions', value: '₹4,50,000', status: 'In Production' },
              { name: 'Dr. John Owen Fellowship', service: 'Puritan Vernacular Monograph', value: '₹2,80,000', status: 'Drafting' },
              { name: 'Grace Baptist Alliance', service: 'Telugu Church Commentary', value: '₹3,20,000', status: 'Theol. Review' },
              { name: 'Sola Gratia Publications', service: 'Malayalam Translation', value: '₹1,90,000', status: 'Completed' },
            ].map((client, idx) => (
              <div
                key={idx}
                className="p-3 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between text-xs"
              >
                <div className="truncate pr-2">
                  <h4 className="font-extrabold text-slate-900 truncate">{client.name}</h4>
                  <span className="text-[10px] font-bold text-slate-500 font-mono block truncate">
                    {client.service}
                  </span>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-black text-slate-900">{client.value}</div>
                  <span className="text-[9px] font-extrabold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                    {client.status}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <Link
            to="/admin/customers"
            className="w-full py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-bold text-center block transition-colors"
          >
            Manage {customersList.length || 4} Client Accounts
          </Link>
        </div>

        {/* Card 3: Internal Staffing & Department Allocation */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200/90 space-y-4 flex flex-col justify-between">
          <div className="space-y-1 border-b border-slate-100 pb-3 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-purple-600" />
                <h3 className="font-extrabold text-base text-slate-900">
                  Internal Staffing
                </h3>
              </div>
              <p className="text-[11px] text-slate-600 font-semibold">
                Linguists, editorial leads &amp; production staff
              </p>
            </div>
            <Link to="/admin/staff" className="text-xs font-bold text-purple-600 hover:underline">
              View All &rarr;
            </Link>
          </div>

          <div className="space-y-2.5">
            {[
              { name: 'Jonathan David', role: 'Executive Editorial Lead', dept: 'EDITORIAL', languages: 'English, Greek' },
              { name: 'Pradeep Kumar', role: 'Senior Vernacular Translator', dept: 'TRANSLATION', languages: 'Tamil, Telugu' },
              { name: 'Rajesh Sharma', role: 'Hindi Translation Specialist', dept: 'TRANSLATION', languages: 'Hindi, Sanskrit' },
              { name: 'Anand Paul', role: 'Theological Review Director', dept: 'EDITORIAL', languages: 'Malayalam, English' },
            ].map((staff, idx) => (
              <div
                key={idx}
                className="p-3 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between text-xs"
              >
                <div className="truncate pr-2">
                  <h4 className="font-extrabold text-slate-900">{staff.name}</h4>
                  <span className="text-[10px] font-bold text-purple-700 block truncate">
                    {staff.role}
                  </span>
                </div>
                <span className="px-2 py-0.5 rounded-md bg-purple-100 text-purple-800 font-extrabold text-[10px] font-mono shrink-0">
                  {staff.languages}
                </span>
              </div>
            ))}
          </div>

          <Link
            to="/admin/staff"
            className="w-full py-2 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-800 text-xs font-bold text-center block transition-colors"
          >
            Manage {staffList.length || 7} Internal Staff
          </Link>
        </div>

      </div>

      {/* 5. Section 3: Reading Demographic + Popular Authors & Partners Table */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Bottom Left: Reading Demographic (8 Cols) */}
        <div className="lg:col-span-8 bg-white rounded-3xl p-6 shadow-sm border border-slate-200/90 space-y-6">
          
          <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
            <h3 className="font-extrabold text-base text-slate-900">
              Reading Demographic &amp; Category Breakdown
            </h3>
            <span className="text-xs font-bold text-slate-600 font-mono">
              FOR THE TRUTH PRESS
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            
            {/* Left 7 Cols: High Contrast Category Bars */}
            <div className="md:col-span-7 space-y-3.5">
              {demographicCategories.map((cat) => (
                <div key={cat.name} className="space-y-1">
                  <div className="flex items-center justify-between text-xs font-extrabold text-slate-900">
                    <span className="truncate pr-2">{cat.name}</span>
                    <span className="font-mono text-[11px] text-slate-700 font-bold shrink-0">{cat.ratio}</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden border border-slate-200">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{ width: `${cat.percent}%`, backgroundColor: cat.color }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Right 5 Cols: SVG Donut Chart with Bold Center Text */}
            <div className="md:col-span-5 flex flex-col items-center justify-center">
              <div className="relative w-48 h-48 flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="38" fill="transparent" stroke="#A855F7" strokeWidth="14" strokeDasharray="35.8 238.7" strokeDashoffset="0" />
                  <circle cx="50" cy="50" r="38" fill="transparent" stroke="#06B6D4" strokeWidth="14" strokeDasharray="31.0 238.7" strokeDashoffset="-37" />
                  <circle cx="50" cy="50" r="38" fill="transparent" stroke="#3B82F6" strokeWidth="14" strokeDasharray="38.2 238.7" strokeDashoffset="-69" />
                  <circle cx="50" cy="50" r="38" fill="transparent" stroke="#D946EF" strokeWidth="14" strokeDasharray="54.9 238.7" strokeDashoffset="-108" />
                  <circle cx="50" cy="50" r="38" fill="transparent" stroke="#84CC16" strokeWidth="14" strokeDasharray="45.3 238.7" strokeDashoffset="-164" />
                  <circle cx="50" cy="50" r="38" fill="transparent" stroke="#F59E0B" strokeWidth="14" strokeDasharray="40.5 238.7" strokeDashoffset="-210" />
                  <circle cx="50" cy="50" r="38" fill="transparent" stroke="#F43F5E" strokeWidth="14" strokeDasharray="31.0 238.7" strokeDashoffset="-252" />
                </svg>

                {/* Donut Inner Label */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                  <span className="text-[11px] text-slate-700 font-extrabold">Total Books</span>
                  <span className="text-3xl font-black text-slate-900 font-serif leading-none mt-0.5">
                    556
                  </span>
                </div>
              </div>

              {/* Legend with High Visibility */}
              <div className="flex flex-wrap justify-center gap-2.5 pt-3 text-[11px] font-mono font-extrabold text-slate-800">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#A855F7]" /> 15%
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#06B6D4]" /> 13%
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#3B82F6]" /> 16%
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#D946EF]" /> 23%
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#84CC16]" /> 19%
                </span>
              </div>
            </div>

          </div>

        </div>

        {/* Bottom Right: Popular Authors & Partners Table (4 Cols) */}
        <div className="lg:col-span-4 bg-white rounded-3xl p-6 shadow-sm border border-slate-200/90 space-y-4 flex flex-col justify-between">
          
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="font-extrabold text-base text-slate-900">
              Popular Authors &amp; Leads
            </h3>
            <Link
              to="/admin/partners"
              className="text-xs font-bold text-[#FF6B4A] hover:underline"
            >
              See all
            </Link>
          </div>

          {/* Table matching screenshot columns: Name | Type | Action */}
          <div className="space-y-3 overflow-x-auto">
            <div className="grid grid-cols-12 text-[10px] font-mono font-extrabold uppercase text-slate-600 pb-1.5 border-b border-slate-200 px-2">
              <span className="col-span-5">Name</span>
              <span className="col-span-4">Type</span>
              <span className="col-span-3 text-right">Action</span>
            </div>

            {[
              { realName: 'Crossway Books', type: 'Rights Holder', initial: 'C', color: 'bg-blue-700' },
              { realName: 'IVP UK & USA', type: 'Publisher', initial: 'I', color: 'bg-purple-700' },
              { realName: 'Donald Fairbairn', type: 'Theol. Author', initial: 'D', color: 'bg-emerald-700' },
              { realName: 'Thomas Schreiner', type: 'Theol. Author', initial: 'T', color: 'bg-amber-700' },
              { realName: 'Banner of Truth', type: 'Classics', initial: 'B', color: 'bg-rose-700' },
              { realName: 'Pradeep Kumar', type: 'Lead Translator', initial: 'P', color: 'bg-indigo-700' },
              { realName: 'Jonathan David', type: 'Senior Editor', initial: 'J', color: 'bg-teal-700' },
            ].map((author, idx) => (
              <div
                key={idx}
                className="grid grid-cols-12 items-center text-xs py-1.5 px-2 rounded-xl hover:bg-slate-50 transition-colors"
              >
                {/* Column 1: Avatar + Name */}
                <div className="col-span-5 flex items-center gap-2 truncate">
                  <div className={`w-7 h-7 rounded-full ${author.color} text-white font-black text-[10px] flex items-center justify-center shrink-0 shadow-xs`}>
                    {author.initial}
                  </div>
                  <span className="font-extrabold text-slate-900 text-xs truncate">
                    {author.realName}
                  </span>
                </div>

                {/* Column 2: Type */}
                <div className="col-span-4 text-[11px] text-slate-700 font-bold truncate">
                  {author.type}
                </div>

                {/* Column 3: Action Button */}
                <div className="col-span-3 text-right">
                  <Link
                    to="/admin/partners"
                    className="px-2.5 py-1 rounded-lg bg-orange-100 hover:bg-orange-200 text-[#EA580C] font-extrabold text-[10px] transition-colors inline-block cursor-pointer shadow-2xs"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="pt-2 text-center border-t border-slate-100">
            <span className="text-[11px] font-bold text-slate-700">
              Official Imprint of <strong className="text-slate-900">FOR THE TRUTH PRESS</strong>
            </span>
          </div>

        </div>

      </div>

    </div>
  );
};
